import { useState } from "react";
import { Loader2 } from "lucide-react";
import { useGrcDashboardChart } from "@/services/dashboard";
import { useFiscalYears } from "@/services/draftCalendar";
import { OccupancyForecast } from "./OccupancyForecast";
import { BarChart } from "./BarChart";
import { OccupancyAttritionChart } from "./OccupancyAttritionChart";
import { OccupancyByBusinessChart } from "./OccupancyByBusinessChart";

export function GrcDashboard() {
  const { data: fiscalYears = [] } = useFiscalYears();
  const activeFiscalYear = fiscalYears.find((fy) => fy.submissionStatus !== "Closed") ?? fiscalYears[0];
  const [selectedFyId, setSelectedFyId] = useState<number | null>(null);
  const fyId = selectedFyId ?? activeFiscalYear?.id ?? 0;

  const { data, isLoading, isError } = useGrcDashboardChart(fyId);

  const onFiscalYearChange = (fyCode: string) => {
    const fy = fiscalYears.find((f) => f.code === fyCode);
    if (fy) setSelectedFyId(fy.id);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-brand" aria-label="Loading GRC dashboard" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <p role="alert" className="dashboard-error-alert">
        Failed to load GRC dashboard data. Please try refreshing the page.
      </p>
    );
  }

  return (
    <div className="pt-4">
      <div className="flex justify-end mb-4">
        <select
          aria-label="Fiscal year"
          value={fiscalYears.find((fy) => fy.id === fyId)?.code ?? activeFiscalYear?.code ?? ""}
          onChange={(e) => onFiscalYearChange(e.target.value)}
          className="dropdown fiscal-year"
        >
          {fiscalYears.map((fy) => (
            <option key={fy.code} value={fy.code}>
              {fy.code}
            </option>
          ))}
        </select>
      </div>

      <section aria-label="DU Occupancy Forecast" className="charts-row mb-6">
        <OccupancyForecast
          fiscalYear={fiscalYears.find((fy) => fy.id === fyId)?.code ?? activeFiscalYear?.code ?? ""}
          tooltipText="Displays inventory, occupancy %, and attrition % across fiscal periods."
          periods={data.forecastPeriods.map((p) => ({
            periodNumber: p.periodNumber,
            periodLabel: p.periodLabel,
            inventory: p.inventory,
            occupancyPct: p.occupancyPct,
            attritionPct: p.attritionPct,
            bookedDays: p.bookedDays,
            actualDays: p.actualDays,
          }))}
        />
      </section>

      <section aria-label="DU Occupancy charts" className="charts-row mb-6">
        <BarChart
          title="DU Occupancy % by Meeting Type"
          tooltipText="Occupancy percentage breakdown by meeting type."
          items={data.occupancyByMeetingType}
        />
        
        <BarChart
          title="DU Occupancy % by Meeting Category"
          tooltipText="Occupancy percentage breakdown by meeting category."
          items={data.occupancyByMeetingCategory}
        />
        
      </section>

      <section aria-label="DU Occupancy by Period" className="charts-row mb-6">
        <OccupancyByBusinessChart
          tooltipText="Occupancy breakdown by sponsoring business across fiscal periods."
          items={data.occupancyByBusinessByPeriod}
        />
        <OccupancyAttritionChart
          tooltipText="Displays inventory, occupancy %, and attrition % across fiscal periods."
          items={data.occupancyAttritionByPeriod}
        />
        
      </section>

      {data.lastUpdated && (
        <div className="last-updated">
          <p>
            <strong>Last updated:</strong> {data.lastUpdated}
          </p>
        </div>
      )}
    </div>
  );
}
